import React, { useState, useEffect } from "react";
import Layout from "../../layout/Main/Layout";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
} from "@mui/material";
import {
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  TrendingUp as TrendingUpIcon,
  People as PeopleIcon,
  Timeline as TimelineIcon,
  Restaurant as RestaurantIcon,
} from "@mui/icons-material";
import { get, put, post } from "../../config/axios";
import { toastMessage } from "../../utils/toastMessage";

const LowFodmapDiet = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [foodPlans, setFoodPlans] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProgressModal, setUserProgressModal] = useState(false);
  const [phaseTransitionModal, setPhaseTransitionModal] = useState(false);
  const [resetProgressModal, setResetProgressModal] = useState(false);
  const [editFoodPlanModal, setEditFoodPlanModal] = useState(false);
  const [selectedFoodPlan, setSelectedFoodPlan] = useState(null);
  const [usersNeedingAttention, setUsersNeedingAttention] = useState([]);

  // Form states
  const [newPhase, setNewPhase] = useState("");
  const [transitionReason, setTransitionReason] = useState("");
  const [resetReason, setResetReason] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch all data in parallel
      const [usersRes, statsRes, foodPlansRes, attentionRes] = await Promise.all([
        get("/api/dashboard/low-fodmap-admin/users"),
        get("/api/dashboard/low-fodmap-admin/stats"),
        get("/api/dashboard/low-fodmap-admin/food-plans"),
        get("/api/dashboard/low-fodmap-admin/users-needing-attention"),
      ]);

      if (usersRes?.success) {
        console.log('Users response:', usersRes.data);
        setUsers(usersRes.data.users || []);
      }
      if (statsRes?.success) {
        console.log('Stats response:', statsRes.data);
        setStats(statsRes.data || {});
      }
      if (foodPlansRes?.success) {
        console.log('Food plans response:', foodPlansRes.data);
        setFoodPlans(foodPlansRes.data.foodPlans || []);
      }
      if (attentionRes?.success) {
        console.log('Attention response:', attentionRes.data);
        setUsersNeedingAttention(attentionRes.data.users || []);
      }

    } catch (error) {
      toastMessage("Failed to fetch data", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleViewUserProgress = async (userId) => {
    try {
      console.log('Fetching progress for user:', userId);
      const response = await get(`/api/dashboard/low-fodmap-admin/users/${userId}/progress`);
      console.log('Progress response:', response);
      if (response?.success) {
        setSelectedUser(response.data);
        setUserProgressModal(true);
      } else {
        console.log('Response not successful:', response);
        toastMessage("Failed to fetch user progress", "error");
      }
    } catch (error) {
      console.error('Error fetching user progress:', error);
      toastMessage("Failed to fetch user progress", "error");
    }
  };

  const handlePhaseTransition = async () => {
    if (!selectedUser || !newPhase || !transitionReason) {
      toastMessage("Please fill all required fields", "error");
      return;
    }

    try {
      const response = await post(
        `/api/dashboard/low-fodmap-admin/users/${selectedUser._id}/force-phase-transition`,
        { newPhase, reason: transitionReason }
      );

      if (response?.success) {
        toastMessage("Phase transition successful", "success");
        setPhaseTransitionModal(false);
        setNewPhase("");
        setTransitionReason("");
        fetchData(); // Refresh data
      }
    } catch (error) {
      toastMessage("Failed to transition phase", "error");
    }
  };

  const handleResetProgress = async () => {
    if (!selectedUser || !resetReason) {
      toastMessage("Please provide a reason", "error");
      return;
    }

    try {
      const response = await post(
        `/api/dashboard/low-fodmap-admin/users/${selectedUser._id}/reset-food-progress`,
        { reason: resetReason }
      );

      if (response?.success) {
        toastMessage("Progress reset successful", "success");
        setResetProgressModal(false);
        setResetReason("");
        fetchData(); // Refresh data
      }
    } catch (error) {
      toastMessage("Failed to reset progress", "error");
    }
  };

  const handleEditFoodPlan = async () => {
    if (!selectedFoodPlan) return;

    try {
      const response = await put(
        `/api/dashboard/low-fodmap-admin/food-plans/${selectedFoodPlan._id}`,
        selectedFoodPlan
      );

      if (response?.success) {
        toastMessage("Food plan updated successfully", "success");
        setEditFoodPlanModal(false);
        setSelectedFoodPlan(null);
        fetchData(); // Refresh data
      }
    } catch (error) {
      toastMessage("Failed to update food plan", "error");
    }
  };

  const getPhaseColor = (phase) => {
    switch (phase) {
      case "elimination": return "warning";
      case "introduction": return "info";
      case "maintenance": return "success";
      default: return "default";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Active": return "success";
      case "Paused": return "warning";
      case "Completed": return "info";
      default: return "default";
    }
  };

  const formatDate = (date) => {
    if (!date) return "Not set";
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return "Invalid Date";
      return dateObj.toLocaleDateString();
    } catch (error) {
      return "Invalid Date";
    }
  };

  const calculateWeeksElapsed = (startDate) => {
    const start = new Date(startDate);
    const now = new Date();
    const diffTime = Math.abs(now - start);
    const diffWeeks = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 7));
    return diffWeeks;
  };

  if (loading) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box>
        <Typography variant="h4" gutterBottom>
          Low FODMAP Diet Management
        </Typography>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <PeopleIcon color="primary" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.totalUsers || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Total Users
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <TimelineIcon color="warning" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.eliminationUsers || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      In Elimination
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <RestaurantIcon color="info" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.introductionUsers || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      In Introduction
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center">
                  <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                  <Box>
                    <Typography variant="h6">{stats.completedUsers || 0}</Typography>
                    <Typography variant="body2" color="textSecondary">
                      Completed
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
            <Tab label="User Management" />
            <Tab label="Food Introduction Plans" />
            <Tab label="Users Needing Attention" />
          </Tabs>
        </Box>

        {/* Tab Content */}
        {activeTab === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Low FODMAP Users
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>User</TableCell>
                      <TableCell>Current Phase</TableCell>
                      <TableCell>Week</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id}>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {user.fullname || "Unknown User"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {user.email || "No email"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.progress?.phase || "elimination"}
                            color={getPhaseColor(user.progress?.phase)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            Week {user.progress?.currentWeek || 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={user.progress?.status || "Active"}
                            color={getStatusColor(user.progress?.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(user.progress?.dietStartDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Tooltip title="View Progress">
                              <IconButton
                                size="small"
                                onClick={() => handleViewUserProgress(user._id)}
                                color="info"
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Force Phase Transition">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setPhaseTransitionModal(true);
                                }}
                                color="warning"
                              >
                                <PlayArrowIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Reset Progress">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedUser(user);
                                  setResetProgressModal(true);
                                }}
                                color="error"
                              >
                                <RefreshIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {activeTab === 1 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Food Introduction Plans
              </Typography>
              <Grid container spacing={2}>
                {foodPlans.map((plan) => (
                  <Grid item xs={12} md={6} key={plan._id}>
                    <Card variant="outlined">
                      <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                          <Typography variant="h6" color="primary">
                            {plan.fodmapCategory}
                          </Typography>
                          <Chip label={`Order: ${plan.order}`} size="small" />
                        </Box>
                        <Typography variant="body2" color="textSecondary" mb={2}>
                          {plan.foodChoices?.length || 0} food choices available
                        </Typography>
                        <Button
                          size="small"
                          startIcon={<EditIcon />}
                          onClick={() => {
                            setSelectedFoodPlan(plan);
                            setEditFoodPlanModal(true);
                          }}
                        >
                          Edit Plan
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        )}

        {activeTab === 2 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Users Needing Attention
              </Typography>
              {usersNeedingAttention.length === 0 ? (
                <Alert severity="info">No users currently need attention</Alert>
              ) : (
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>User</TableCell>
                        <TableCell>Issue</TableCell>
                        <TableCell>Current Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {usersNeedingAttention.map((user) => (
                        <TableRow key={user._id}>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              {user.fullname || "Unknown User"}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={user.issue || "Phase transition needed"}
                              color="warning"
                              size="small"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {user.progress?.phase} - Week {user.progress?.currentWeek}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Button
                              size="small"
                              onClick={() => handleViewUserProgress(user._id)}
                            >
                              Review
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        )}
      </Box>

      {/* User Progress Modal */}
      <Dialog
        open={userProgressModal}
        onClose={() => setUserProgressModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>User Progress Details</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    User Information
                  </Typography>
                  <Typography variant="body2">
                    <strong>Name:</strong> {selectedUser.fullname}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Email:</strong> {selectedUser.email}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phone:</strong> {selectedUser.phone || "Not provided"}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="h6" gutterBottom>
                    Diet Progress
                  </Typography>
                  <Typography variant="body2">
                    <strong>Phase:</strong> {selectedUser.progress?.phase}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Week:</strong> {selectedUser.progress?.currentWeek}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Status:</strong> {selectedUser.progress?.status}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Start Date:</strong> {formatDate(selectedUser.progress?.dietStartDate)}
                  </Typography>
                </Grid>
                {selectedUser.progress?.currentFoodIntroduction && (
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Current Food Introduction
                    </Typography>
                    <Typography variant="body2">
                      <strong>Category:</strong> {selectedUser.progress.currentFoodIntroduction.fodmapCategory}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Food:</strong> {selectedUser.progress.currentFoodIntroduction.foodName}
                    </Typography>
                    <Typography variant="body2">
                      <strong>Day:</strong> {selectedUser.progress.currentFoodIntroduction.currentDay}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserProgressModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Phase Transition Modal */}
      <Dialog
        open={phaseTransitionModal}
        onClose={() => setPhaseTransitionModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Force Phase Transition</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>New Phase</InputLabel>
              <Select
                value={newPhase}
                onChange={(e) => setNewPhase(e.target.value)}
                label="New Phase"
              >
                <MenuItem value="elimination">Elimination</MenuItem>
                <MenuItem value="introduction">Introduction</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Reason for Transition"
              value={transitionReason}
              onChange={(e) => setTransitionReason(e.target.value)}
              multiline
              rows={3}
              placeholder="Explain why this phase transition is necessary..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhaseTransitionModal(false)}>Cancel</Button>
          <Button onClick={handlePhaseTransition} variant="contained" color="warning">
            Force Transition
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset Progress Modal */}
      <Dialog
        open={resetProgressModal}
        onClose={() => setResetProgressModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reset User Progress</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will reset the user's food introduction progress. This action cannot be undone.
            </Alert>
            <TextField
              fullWidth
              label="Reason for Reset"
              value={resetReason}
              onChange={(e) => setResetReason(e.target.value)}
              multiline
              rows={3}
              placeholder="Explain why this reset is necessary..."
              required
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetProgressModal(false)}>Cancel</Button>
          <Button onClick={handleResetProgress} variant="contained" color="error">
            Reset Progress
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Food Plan Modal */}
      <Dialog
        open={editFoodPlanModal}
        onClose={() => setEditFoodPlanModal(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Edit Food Introduction Plan</DialogTitle>
        <DialogContent>
          {selectedFoodPlan && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedFoodPlan.fodmapCategory}
              </Typography>
              <Typography variant="body2" color="textSecondary" mb={2}>
                Order: {selectedFoodPlan.order}
              </Typography>
              
              <Typography variant="subtitle1" gutterBottom>
                Food Choices
              </Typography>
              {selectedFoodPlan.foodChoices?.map((food, index) => (
                <Accordion key={index} sx={{ mb: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="body1" fontWeight="bold">
                      {food.foodName}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Day 1 Amount"
                          value={food.day1Amount?.description || ""}
                          onChange={(e) => {
                            const updatedPlan = { ...selectedFoodPlan };
                            updatedPlan.foodChoices[index].day1Amount.description = e.target.value;
                            setSelectedFoodPlan(updatedPlan);
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Day 2 Amount"
                          value={food.day2Amount?.description || ""}
                          onChange={(e) => {
                            const updatedPlan = { ...selectedFoodPlan };
                            updatedPlan.foodChoices[index].day2Amount.description = e.target.value;
                            setSelectedFoodPlan(updatedPlan);
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Day 3 Amount"
                          value={food.day3Amount?.description || ""}
                          onChange={(e) => {
                            const updatedPlan = { ...selectedFoodPlan };
                            updatedPlan.foodChoices[index].day3Amount.description = e.target.value;
                            setSelectedFoodPlan(updatedPlan);
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Notes"
                          value={food.notes || ""}
                          onChange={(e) => {
                            const updatedPlan = { ...selectedFoodPlan };
                            updatedPlan.foodChoices[index].notes = e.target.value;
                            setSelectedFoodPlan(updatedPlan);
                          }}
                          multiline
                          rows={2}
                        />
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditFoodPlanModal(false)}>Cancel</Button>
          <Button onClick={handleEditFoodPlan} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default LowFodmapDiet;
